import { Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { AdvisoryService } from "../../../Component/Api/Advisory/AdvisoryService";
import { DataTable } from "../../../Component/Common/Components/DataTable/DataTable";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { EntriesPerPage } from "../../../Component/Model/Table/Table";
import { ClientScorecard, ClientScorecardSearchRequest } from "../../../Component/Model/Dto/Advisory/ClientScorecard";
import { useParams, useLocation } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import { ScorecardAtom } from "../../../State/Scorecard.Atom";
import { EEScorecardAtom } from "../../../State/EEScorecard.Atom";
import { ClientRegistrationAtom } from "../../../State/Advisory/ClientRegistration.Atom";
import { ClientScorecardDataTableColumns } from "./ClientScorecardsDataTableColumns";

interface RouteParams {
    registrationId: string;
    registrationName: string;
}

export const ClientScorecards = () => {
    const [scorecardData, setScorecardData] = useState<ClientScorecard[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
    const [totalItemCount, setTotalItemCount] = useState<number>(0);
    const { registrationId, registrationName } = useParams<RouteParams>();
    const location = useLocation();
    const isEEModule = location.pathname.includes('/ee/clientScorecards');
    const [clientRegistration, setClientRegistration] = useRecoilState(ClientRegistrationAtom);
    const resetScorecard = useResetRecoilState(ScorecardAtom);
    const resetEEScorecard = useResetRecoilState(EEScorecardAtom);

    const clientScorecardSearchRequest: ClientScorecardSearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        columnSortOptions: columnSortOptions
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }
    
    useEffect(() => {
        resetScorecard();
        resetEEScorecard();

        if (clientRegistration.clientRegistrationId !== registrationId) {
            setClientRegistration({
                clientRegistrationId: registrationId,
                clientRegistrationName: registrationName
            });
        }
    }, []);

    useEffect(() => {
        setPageNumber(1);
        setSearchValue('');
        setColumnSortOptions([]);
        setScorecardData([]);
    }, [isEEModule]);

    useEffect(() => {
        if (registrationId != ':registrationId') {
            fetchClientScorecards();
        }
    }, [entriesPerPage,
        pageNumber,
        columnSortOptions,
        searchValue,
        isEEModule
    ]);


    const fetchClientScorecards = async () => {
        setIsLoading(true);
        try {
            const clientScorecardResult = await AdvisoryService.searchClientScorecards(registrationId, isEEModule, clientScorecardSearchRequest);
            
            setScorecardData(clientScorecardResult.data.results);
            setFilteredItemCount(clientScorecardResult.data.filteredItemsCount);
            setTotalItemCount(clientScorecardResult.data.totalItemsCount);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const clientScorecardTable = ClientScorecardDataTableColumns(scorecardData);
    return (
        <>
            <Typography variant="h4" style={{ paddingBottom: "20px" }}>
                {isEEModule ? 'Client EE Scorecards' : 'Client BEE Scorecards'}
            </Typography>

            <DataTable
                table={clientScorecardTable}
                entriesPerPage={entriesPerPage}
                pageNumber={pageNumber}
                filteredItemsCount={filteredItemCount}
                totalItemsCount={totalItemCount}
                isLoading={isLoading}
                manualSorting={true}
                canSearch
                searchValueChanged={searchValueChanged}
                entriesPerPageChanged={entriesPerPageChanged}
                pageNumberChanged={pageNumberChanged}
                columnSortingChanged={columnSortingChanged} />


        </>
    )
}

